import React from 'react';
import {connect} from 'react-redux';
import webservice from '../common/Webservice';
import PropTypes from 'prop-types';
import {ResponsivePie} from '@nivo/pie';
import {ResponsiveLine} from '@nivo/line';
import {COBI_HIERARCHY, COLOR_PALETTE, contentLength, footerNotice, formatAmount, formatCobiYear, MAX_CHARACTERS, MEDIA_SIZES} from "../misc/Shared";
import {format} from "d3-format";
import Media from "react-media";
import {isEmpty, remove} from "lodash";
import {AjaxSpinner} from "@utahdts/dts-react-common";

const propTypes = {
	currentCobiId:  PropTypes.number,

	currentTab:  PropTypes.string,

	currentYear:  PropTypes.number,

	draft: PropTypes.bool,

	menuObj: PropTypes.object,
};

const defaultProps = {
};

// Props connected to the store
const mapStateToProps = state => ({
	currentTab: state.app.currentTab,
	currentCobiId: state.app.currentCobiId,
	currentYear: state.app.currentYear,
	draft: state.app.draft,
	menuObj: state.menu.menuObj,
});

class Overview extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			appropriatedSourcesChart: [],
			appropriatedSourcesChartFill: [],
			busy: true,
			consumerIndexChart: [],
			content: undefined,
			operatingHierarchyChart: [],
			operatingHierarchyChartFill: [],
			yScale: {min: -1, max: 1},
			printing: false
		};

		this.setContent = this.setContent.bind(this);
		this.getContent = this.getContent.bind(this);

		this.getContent();

		this.hierarchy = [
			{
				text: 'Subcommittees',
				level: COBI_HIERARCHY.SUBCOMMITTEE,
			},
			{
				text: 'Agencies',
				level: COBI_HIERARCHY.AGENCY,
			},
			{
				text: 'Line Items',
				level: COBI_HIERARCHY.LINE_ITEM,
			},
			{
				text: 'Programs',
				level: COBI_HIERARCHY.PROGRAM,
			},
		];

		this.defs = [
			{
				"id": "lines",
				"type": "patternLines",
				"background": "inherit",
				"color": "rgba(255, 255, 255, 0.3)",
				"rotation": -45,
				"lineWidth": 6,
				"spacing": 10
			}
		];
	}

	componentDidMount() {
		window.onbeforeprint = () => this.setState({printing: true});
		window.onafterprint = () => this.setState({printing: false});
	}

	componentDidUpdate(prevProps) {
		if ((prevProps.currentCobiId !== this.props.currentCobiId) || (this.props.currentYear !== prevProps.currentYear)) {
			this.getContent();
		}
	}

	componentWillUnmount() {
		this.isCancelled = true;
	}

	getContent() {
		this.props.draft ?
			webservice.admin.tab.draft(this.props.currentCobiId, this.props.currentYear, this.props.currentTab).then(this.setContent)
			: webservice.public.tab.content(this.props.currentCobiId, this.props.currentYear, this.props.currentTab).then(this.setContent);
	}

	setContent(data) {
		if(!this.isCancelled) {
			let appropriatedSourcesChart = this.formatPieData(data.appropriatedSourcesChart);
			let operatingHierarchyChart = this.formatPieData(data.operatingHierarchyChart);
			this.setState({
				content: data.text,
				busy: false,
				appropriatedSourcesChart: appropriatedSourcesChart.data,
				appropriatedSourcesChartFill: appropriatedSourcesChart.fill,
				consumerIndexChart: this.formatLineData(data.consumerIndexChart),
				operatingHierarchyChart: operatingHierarchyChart.data,
				operatingHierarchyChartFill: operatingHierarchyChart.fill
			});
		}
	}

	formatPieData(data) {
		let i = 0, j = 0, fill = [];
		data = remove(data, item => (item.value >= 0));
		data.forEach(item => {
			i = i >= (COLOR_PALETTE.length) ? 0 : i;
			item.id = item.title;
			item.label = item.title;
			item.color = COLOR_PALETTE[i];
			item.colorClass = 'background--color' + i;

			if( j >= (COLOR_PALETTE.length) ) {
				fill.push({
					"match": {
						"id": item.title
					},
					"id": "lines"
				});
				//item.colorClass += ' lines';
			}

			i++; j++;
		});
		return {
			data: data,
			fill: fill
		};
	}

	formatLineData(data) {
		let i = 0;
		let lines = [];
		let lineData = [];
		let cpiList = data["cpiList"];
		let yScale = [];
		cpiList.forEach(cpi => {
			i = i >= (COLOR_PALETTE.length - 1) ? 0 : i;
			if(!lines.includes(cpi.orgName)) {
				lines.push(cpi.orgName);
				lineData.push({
					id: cpi.orgName,
					title: cpi.orgName,
					color: COLOR_PALETTE[i],
					colorClass: 'background--color' + i,
					data: [
						{
							x: cpi.year,
							y: cpi.adjustedChange.toFixed(3)
						}
					]
				});
				yScale.push(cpi.adjustedChange.toFixed(3));
				i++;
			} else {
				let currentLine = lineData.find(item => item.id === cpi.orgName);
				currentLine && currentLine.data.push({
					x: cpi.year,
					y: cpi.adjustedChange.toFixed(3)
				});
				yScale.push(cpi.adjustedChange.toFixed(3));
			}
		});
		let min = Math.floor(Math.min(...yScale) * 10) * .1;
		let max = Math.ceil(Math.max(...yScale) * 10) * .1;
		this.setState({yScale: {min:  min, max: max}});
		return lineData;
	}

	getHierarchyChartTitle() {
		let child = 0;
		this.hierarchy.forEach((item, index) => {
			const activeItem = this.props.menuObj[item.level] && this.props.menuObj[item.level].find(element => element.selected === true);
			child = activeItem ? index + 1 : child;

		});
		child = child >= this.hierarchy.length ? "Program" : this.hierarchy[child].text;
		return child;
	}

	renderLegend(data) {
		return (
			<div className="legend">
				{data.map(item => (
					<div key={item.title} className="legend--item">
						<div className={"legend--item--symbol " + item.colorClass}/>
						<span>{item.title} {typeof item.value === 'number' ? <em>(${formatAmount(item.value, 0)})</em> : undefined}</span>
					</div>
				))}
			</div>
		);
	}

	readMore(event, show) {
		this.setState({readMore: show});

		!show && window.scroll({top: event.target.offsetTop, left: 0});
	}

	formatTotal(number) {
		let specifier;
		let digitTotal = number.toString().length % 3;
		switch (digitTotal) {
			case 0:
				specifier = '$.5s';
				break;
			case 1:
				specifier = '$.3s';
				break;
			case 2:
				specifier = '$.4s';
				break;
		}
		return format(specifier)(number).replace(/G/, "B");
	}

	render() {
		return (
			<React.Fragment>
				{ this.state.busy && <AjaxSpinner size={30} className="mb-spacing"/>}
				{ this.state.content &&
				<section className={"fr-view"}>
					<div className="clearfix">
						<Media query={MEDIA_SIZES.TABLET_NARROW_MAX_WIDTH}>
							{matches =>
								matches ? (
									<React.Fragment>
										<div dangerouslySetInnerHTML={{__html: this.state.content.tabHtml}}  className={contentLength(this.state.content.tabHtml, MAX_CHARACTERS) ? this.state.readMore ? "mb-spacing" : "read-more" : "mb-spacing"}/>
										{contentLength(this.state.content.tabHtml, MAX_CHARACTERS) && (!this.state.readMore ?
											<a onClick={(e) => this.readMore(e, true)} className="mb-spacing bold-text inline-block atid_read-more-link">
												READ MORE <span className="ml-spacing-small">&raquo;</span>
											</a>
											:
											<a onClick={(e) => this.readMore(e, false)}  className="mb-spacing bold-text inline-block atid_read-more-link">
												READ LESS <span className="ml-spacing-small">&raquo;</span>
											</a>
										)}
									</React.Fragment>
								) : (
									<div dangerouslySetInnerHTML={{__html: this.state.content.tabHtml}}/>
								)
							}
						</Media>


					</div>
					<div className="flex no-flex-mobile no-flex-print pagebreak direction-col-print">
						{!isEmpty(this.state.appropriatedSourcesChart) && <div className="flex1 flex direction-col p-spacing no-flex-print">
							<h3 className="center-text">{formatCobiYear(this.props.currentYear)} Sources - {this.formatTotal(this.state.appropriatedSourcesChart.reduce((a, b) => a + b.value, 0))}</h3>
							<div className="pie-graph">
								<ResponsivePie
									data={this.state.appropriatedSourcesChart}
									margin={{
										"top": 20,
										"right": 20,
										"bottom": 20,
										"left": 20
									}}
									innerRadius={0.4}
									padAngle={1}
									enableRadialLabels={false}
									enableSlicesLabels={false}
									colors={d => d.data.color}
									tooltip={(datum) => {
										let data = datum.datum;
										return <div className="tooltip">{data.label}: <strong>${formatAmount(data.value, 0)}</strong></div>;
									}}
									height={350}
									//defs={this.defs}
									fill={this.state.appropriatedSourcesChartFill}
									enableArcLabels={false}
									enableArcLinkLabels={false}
								/>
							</div>
							{this.renderLegend(this.state.appropriatedSourcesChart)}
						</div>}
						{!isEmpty(this.state.operatingHierarchyChart) && <div className="flex1 flex direction-col p-spacing no-flex-print">
							<h3 className="center-text">{this.getHierarchyChartTitle()}</h3>
							<div className="pie-graph">
								<ResponsivePie
									data={this.state.operatingHierarchyChart}
									margin={{
										"top": 20,
										"right": 20,
										"bottom": 20,
										"left": 20
									}}
									innerRadius={0.4}
									padAngle={1}
									enableRadialLabels={false}
									enableSlicesLabels={false}
									colors={d => d.data.color}
									tooltip={(datum) => {
										let data = datum.datum;
										return <div className="tooltip">{data.label}: <strong>${formatAmount(data.value, 0)}</strong></div>;
									}}
									height={350}
									//defs={this.defs}
									fill={this.state.operatingHierarchyChartFill}
									enableArcLabels={false}
									enableArcLinkLabels={false}
								/>
							</div>
							{this.renderLegend(this.state.operatingHierarchyChart)}
						</div>}
					</div>
					{!isEmpty(this.state.consumerIndexChart) &&<div className="pagebreak">
						 <h3 className="center-text">Growth Index</h3>
						<div className="bar-graph" style={this.state.printing ? {width: '8.5in'} : undefined}>
							<ResponsiveLine
								data={this.state.consumerIndexChart}
								margin={{ top: 50, right: 50, bottom: 50, left: 50 }}
								xScale={{ type: 'point' }}
								yScale={{ type: 'linear', stacked: false, min: this.state.yScale.min, max: this.state.yScale.max }}
								axisBottom={{
									orient: "bottom",
									tickSize: 5,
									tickPadding: 5,
									tickRotation: window.outerWidth < 767 ? 90 : 0,
									legend: "",
									legendOffset: 36,
									legendPosition: "middle"
								}}
								axisLeft={{
									orient: "left",
									tickSize: 5,
									tickPadding: 5,
									tickRotation: 0,
									legend: "",
									legendOffset: -40,
									legendPosition: "middle"
								}}
								enableGridX={false}
								pointSize={10}
								pointBorderWidth={2}
								pointBorderColor="#ffffff"
								pointLabel="y"
								pointLabelYOffset={-12}
								motionStiffness={90}
								motionDamping={15}
								enableStackTooltip={true}
								colors={d => d.color}
								isInteractive={true}
								enableCrosshair={true}
								useMesh={true}
								enableSlices={'x'}
								animate={false}
							/>
						</div>
						<div style={{fontSize: '.85em', fontStyle: 'italic', textAlign: 'center'}}>This graph tracks the inflation adjusted per capita operating and capital budgets, including expendable revenue accounts and funds, from all sources as indexed to the first year displayed.</div>
						{this.renderLegend(this.state.consumerIndexChart)}
					</div>}
					{footerNotice('mt-spacing-small')}
				</section> }
			</React.Fragment>
		);
	}
}

Overview.propTypes = propTypes;
Overview.defaultProps = defaultProps;

export default connect(mapStateToProps)(Overview);
